import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MinusIcon, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/components/Layout";
import { Reveal, RevealLines, RevealHeading, EASE } from "@/components/motion-primitives";
import { SectionLabel } from "@/components/ui-blocks";

const API = process.env.REACT_APP_BACKEND_URL;

const AccordionItem = ({ index, question, answer, isOpen, onToggle }) => (
  <div className="border-t border-border" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      data-testid={`faq-toggle-${index}`}
      className="w-full flex items-start justify-between gap-4 py-5 text-left group"
    >
      <span className="flex items-start gap-4 min-w-0 flex-1">
        <span className="font-mono-alt text-[11px] text-accentbrand shrink-0 pt-1.5">{String(index + 1).padStart(2, "0")}</span>
        <span className="font-display text-lg sm:text-xl font-semibold text-ink group-hover:text-accentbrand transition-colors">{question}</span>
      </span>
      <span className={`shrink-0 mt-1 w-9 h-9 border border-ink flex items-center justify-center transition-all ${isOpen ? "bg-night text-white rotate-180" : "bg-transparent text-ink group-hover:bg-night group-hover:text-white"}`}>
        {isOpen ? <MinusIcon size={16} /> : <Plus size={16} />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="overflow-hidden"
        >
          <div className="pb-6 pl-10 pr-2 max-w-3xl">
            <p className="text-muted-foreground leading-relaxed">{answer}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const AskQuestionForm = ({ f }) => {
  const [form, setForm] = useState({ first_name: "", email: "", question: "", consent: false, company: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/api/faq/question`, form, { timeout: 10000 });
      setStatus("success");
      setForm({ first_name: "", email: "", question: "", consent: false, company: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || f.askError);
    }
  };

  if (status === "success") {
    return (
      <div className="border border-border bg-card p-8 text-center" data-testid="faq-form-success">
        <CheckCircle2 size={40} className="text-accentbrand mx-auto mb-3" />
        <p className="font-display text-lg font-bold text-ink">{f.askSuccess}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-border bg-card p-6 sm:p-8 space-y-4" data-testid="faq-form">
      <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground">{f.askFirstName} *</span>
          <input
            type="text"
            required
            data-testid="faq-first-name"
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            className="mt-1 w-full bg-transparent border-b border-border focus:border-ink py-2 text-ink outline-none transition-colors"
          />
        </label>
        <label className="block">
          <span className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground">{f.askEmail} *</span>
          <input
            type="email"
            required
            data-testid="faq-email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="mt-1 w-full bg-transparent border-b border-border focus:border-ink py-2 text-ink outline-none transition-colors"
          />
        </label>
      </div>

      <label className="block">
        <span className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground">{f.askQuestion} *</span>
        <textarea
          required
          rows={4}
          minLength={10}
          data-testid="faq-question"
          value={form.question}
          onChange={(e) => set("question", e.target.value)}
          className="mt-1 w-full bg-transparent border border-border focus:border-ink p-3 text-ink outline-none transition-colors resize-none"
        />
      </label>

      <label className="flex items-start gap-3 cursor-pointer text-sm text-muted-foreground">
        <input
          type="checkbox"
          required
          data-testid="faq-consent"
          checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 w-4 h-4 accent-accentbrand"
        />
        <span>{f.askConsent}</span>
      </label>

      {status === "error" && <p className="text-sm text-red-600" data-testid="faq-form-error">{errorMsg}</p>}

      <button
        type="submit"
        disabled={status === "sending" || !form.consent}
        data-testid="faq-submit"
        className="hover-arrow inline-flex items-center gap-2 bg-accentbrand text-white text-sm font-bold uppercase tracking-wide px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E63E00] transition-colors"
      >
        {status === "sending" ? "Envoi…" : f.askSubmit} <Send size={15} />
      </button>
    </form>
  );
};

export default function FAQ() {
  const { t } = useLang();
  const f = t.faq;
  useSeo(f.seoTitle, f.seoDesc);
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-8 lg:pb-12">
        <SectionLabel>{f.label}</SectionLabel>
        <RevealLines as="h1" delay={0.1} className="display-hero text-ink mt-4" lines={f.heroLines} />
        <Reveal delay={0.4}><p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{f.sub}</p></Reveal>
      </section>

      {/* ACCORDION */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pb-10 lg:pb-16">
        <div className="border-b border-border" data-testid="faq-list">
          {f.items.map((item, i) => (
            <AccordionItem
              key={i}
              index={i}
              question={item.q}
              answer={item.a}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </section>

      {/* ASK A QUESTION */}
      <section className="bg-secondary/50 border-t border-border">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
          <div className="mb-6">
            <SectionLabel>{f.askLabel}</SectionLabel>
            <RevealHeading as="h2" text={f.askHeading} className="mt-1 display-2 text-ink" />
            <Reveal delay={0.1}><p className="mt-3 text-muted-foreground max-w-2xl">{f.askSub}</p></Reveal>
          </div>
          <Reveal delay={0.15}><AskQuestionForm f={f} /></Reveal>
        </div>
      </section>
    </div>
  );
}
